import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import { SECONDARY_CATEGORY_LIST_PAGE_SIZE } from '../../constants'
import { useQueryReportAccessSecondaryCategories } from '../../models/useQueryReportAccessSecondaryCategories'

export interface IUseFirstReportAccessSecondaryCategoryPresenterParams {
  /** `undefined` while no PrimaryCategory is in scope; the query stays idle */
  primaryCategoryId: number | undefined
  /** The unfiltered list page to read the first SecondaryCategory from */
  page: number
}

/**
 * Provides the first SecondaryCategory on one unfiltered page of the
 * PrimaryCategory's list so the Feature Set can derive its default selection
 * (BHV-007, BHV-021). It uses the same query key as the list presenter on
 * that page, so React Query dedupes the request. Data kept from a previous
 * PrimaryCategory or page is never offered as this one's first.
 */
export const useFirstReportAccessSecondaryCategoryPresenter = ({
  primaryCategoryId,
  page,
}: IUseFirstReportAccessSecondaryCategoryPresenterParams) => {
  const {
    secondaryCategoryConfigs,
    pagination,
    isFetching,
    isPreviousData,
    error,
  } = useQueryReportAccessSecondaryCategories(primaryCategoryId, {
    page,
    pageSize: SECONDARY_CATEGORY_LIST_PAGE_SIZE,
    search: undefined,
  })

  const firstSecondaryCategoryConfig: SecondaryCategoryConfig | undefined =
    isPreviousData ? undefined : secondaryCategoryConfigs[0]

  return {
    data: {
      firstSecondaryCategoryConfig,
    },
    states: {
      isLoading: isFetching && !firstSecondaryCategoryConfig,
      isError: Boolean(error) && !firstSecondaryCategoryConfig,
      // The PrimaryCategory has no SecondaryCategories at all, as opposed
      // to a failed or pending load
      isEmpty:
        !isFetching && !error && !isPreviousData && pagination?.count === 0,
    },
  }
}
